// import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import type { AnyObject } from 'types/AnyObject';
import type { FilterComponentProps } from 'uikit';
import { agGridAdapter, Button, IconSearch, Loader, TableFilterContainer, TextField, useDebounce } from 'uikit';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SelectionChangedEvent } from 'ag-grid-community';
import type { AgGridReact, CustomFilterProps } from 'ag-grid-react';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Styles from './CustomTableFilter.module.css';

type TCustomFilterWithGrid = {
    items: Record<string, any>[] | null | undefined;
    requestVariables: Record<string, any>;
    requestFn: (variables: Record<string, any>) => PayloadAction;
    requestQuery: string | TypedDocumentNode<any, any>;
    dataAccessor: string;
    childFieldAccessor: string;
    columnDefs: {
        headerName: string;
        checkboxSelection?: boolean;
        hide?: boolean;
        rowGroup?: boolean;
        lockVisible?: boolean;
    }[];
    filteredGroupHelper?: (rawData: AnyObject[]) => AnyObject[];
    whereVariables: string[];
    withSelectAllBtn?: boolean;
    isClientSideSearch?: boolean;
    additionalWhereVariables?: AnyObject | null;
    withSearch?: boolean;
    hideTableHeader?: boolean;
    customWhereVariablesGetter?: (searchStr: string) => any;
    fieldName: string | undefined;
    resetSearchRef?: React.MutableRefObject<(() => any) | undefined>;
} & FilterComponentProps &
    CustomFilterProps;
const getWhereVariables = (fieldsForSearch: string[], searchText: string) =>
    fieldsForSearch.map((field) => ({
        [field]: {
            containsi: searchText || '',
        },
    }));

export const CustomFilterWithGrid: React.FC<TCustomFilterWithGrid> = (props) => {
    const dispatch = useDispatch();

    const {
        items,
        requestFn,
        requestQuery,
        requestVariables,
        dataAccessor,
        childFieldAccessor,
        columnDefs,
        whereVariables,
        filteredGroupHelper,
        withSelectAllBtn = false,
        isClientSideSearch = false,
        additionalWhereVariables = null,
        withSearch = true,
        hideTableHeader = false,
        customWhereVariablesGetter,
        fieldName,
        context,
        resetSearchRef,
        ...restProps
    } = props;

    const stubHandler = () => undefined;
    const gridRef = useRef<AgGridReact>(null);

    const styleOptions = agGridAdapter({
        size: 'm',
        borderBetweenColumns: true,
        borderBetweenRows: true,
        className: columnDefs?.length === 1 ? Styles.customStyleFilterTable : '',
    });

    const [textFieldValue, setTextFieldValue] = useState<string>('');
    const [allItemSelected, setAllItemsSelected] = useState(false);
    const [isSelectedRow, setIsSelectedRow] = useState<boolean>(false);

    const onChangeTextFieldHandler = useCallback(({ value }) => {
        setTextFieldValue(value);
    }, []);

    useImperativeHandle(resetSearchRef, () => () => setTextFieldValue(''), []);

    const [filteredItemsData, filteredItemsStatus, , filteredItemsRequest] = useRequest({
        requestId: 'filteredItemsData',
        query: requestQuery,
        withEffect: false,
    });

    const filteredItems = useMemo(() => {
        if (isClientSideSearch && items && textFieldValue) {
            return items?.filter(
                (item) =>
                    ('label' in item ? item.label : item[childFieldAccessor])
                        ?.toLowerCase()
                        .includes(textFieldValue.toLowerCase()),
            );
        }

        if (filteredGroupHelper) {
            return filteredGroupHelper(filteredItemsData?.[dataAccessor]?.items);
        }

        return filteredItemsData?.[dataAccessor]?.items;
    }, [
        isClientSideSearch,
        items,
        textFieldValue,
        filteredGroupHelper,
        filteredItemsData,
        dataAccessor,
        childFieldAccessor,
    ]);

    const debouncedSearch = useDebounce(() => {
        dispatch(
            filteredItemsRequest({
                ...requestVariables,
                where: {
                    ...additionalWhereVariables,
                    ...customWhereVariablesGetter?.(textFieldValue),
                    ...(!customWhereVariablesGetter && { or: getWhereVariables(whereVariables, textFieldValue) }),
                },
            }),
        );
    }, 1000);

    useEffect(() => {
        if (textFieldValue && !isClientSideSearch) {
            debouncedSearch();
        }
    }, [debouncedSearch, isClientSideSearch, textFieldValue]);

    const emptySearchStringWhereVariables = useMemo(
        () => additionalWhereVariables || undefined,
        [additionalWhereVariables],
    );
    const requestVariablesKey = useMemo(() => JSON.stringify(requestVariables ?? null), [requestVariables]);
    const loadedVariablesKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!requestQuery || loadedVariablesKeyRef.current === requestVariablesKey) {
            return;
        }

        loadedVariablesKeyRef.current = requestVariablesKey;
        dispatch(
            requestFn({
                ...requestVariables,
                where: emptySearchStringWhereVariables,
            }),
        );
    }, [dispatch, emptySearchStringWhereVariables, requestFn, requestQuery, requestVariables, requestVariablesKey]);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver({});

    const confirmHandler = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            const checkedNodes = api.getSelectedNodes().map((node) => ({
                ...node,
                name: node.key || node.data[childFieldAccessor],
            }));

            const checkedValues = checkedNodes.map((item: { name: string | null; data: any }) => ({
                value: item?.name,
                data: item?.data,
            }));

            if (context?.tableUniqueId) {
                handleSaveFilterState({
                    key: fieldName,
                    newState: checkedValues,
                    rootKey: context?.tableUniqueId,
                });
            }

            restProps?.onConfirm(checkedNodes);
        }
    }, [childFieldAccessor, context?.tableUniqueId, fieldName, handleSaveFilterState, restProps]);

    const handleSelectAllRows = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            api?.selectAll();
        }
    }, []);

    const handleDeselectAllRows = useCallback(() => {
        if (gridRef?.current) {
            const { api } = gridRef.current;

            api?.deselectAll();
        }
    }, []);

    const actualFilterSavedValues = useMemo(() => {
        if (restProps.filterValue) {
            return restProps.filterValue;
        }

        if (context?.tableUniqueId) {
            return fieldName
                ? Object.values(savedFilterState?.[context?.tableUniqueId ?? '']?.[fieldName] ?? {})
                : undefined;
        }

        return fieldName ? Object.values(savedFilterState?.[fieldName ?? ''] ?? {}) : undefined;
    }, [context?.tableUniqueId, fieldName, restProps.filterValue, savedFilterState]);

    const setCheckboxes = useCallback(() => {
        if (Array.isArray(actualFilterSavedValues) && actualFilterSavedValues.length && gridRef.current) {
            gridRef.current.api?.forEachNode((node) => {
                const shouldSelect = (actualFilterSavedValues as any[]).some((selectedFilterItem) => {
                    if (R.equals(selectedFilterItem.data, node.data)) {
                        return true;
                    }

                    const nodeValue = node.data?.[childFieldAccessor];

                    return nodeValue != null && selectedFilterItem?.value === nodeValue;
                });

                if (node.isSelected() !== shouldSelect) {
                    node.setSelected(shouldSelect);
                }
            });

            setIsSelectedRow(true);
        }
    }, [actualFilterSavedValues, childFieldAccessor]);

    const onGridReady = useCallback(() => {
        setCheckboxes();

        if (hideTableHeader) {
            gridRef.current?.api?.setGridOption('headerHeight', 0);
        }
    }, [hideTableHeader, setCheckboxes]);

    useEffect(() => {
        if (filteredItems) {
            setCheckboxes();
        }
        if (!textFieldValue) {
            setCheckboxes();
        }
    }, [setCheckboxes, filteredItems, items, textFieldValue]);

    const onRowsSelected = useCallback(
        (event: SelectionChangedEvent) => {
            setAllItemsSelected(event.api.getSelectedRows().length === items?.length);
            setIsSelectedRow(Boolean(event.api.getSelectedRows().length));
        },
        [items?.length],
    );

    return (
        <>
            {!items && !filteredItems && (
                <TableFilterContainer onConfirm={stubHandler} onCancel={restProps?.onCancel}>
                    <div className={Styles.loaderHeight}>
                        <Loader />
                    </div>
                </TableFilterContainer>
            )}
            {(items || filteredItems) && (
                <TableFilterContainer
                    className={Styles.tableFilterContainer}
                    onConfirm={confirmHandler}
                    onCancel={restProps?.onCancel}
                >
                    {withSearch && (
                        <TextField
                            className="TableTextFilter-Searchbar"
                            onChange={onChangeTextFieldHandler}
                            value={textFieldValue}
                            type="text"
                            placeholder="Найти в списке"
                            size="s"
                            leftSide={IconSearch}
                        />
                    )}
                    {withSelectAllBtn && (
                        <div className="TableTextFilter-Controls">
                            <Button
                                label="Выбрать все"
                                onClick={handleSelectAllRows}
                                className="Button Button_size_m Button_view_clear Button_width_default Button_form_brick MixFocus select-button"
                                disabled={allItemSelected}
                            />
                            <Button
                                label="Сбросить"
                                onClick={handleDeselectAllRows}
                                className="Button Button_size_m Button_view_clear Button_width_default Button_form_brick MixFocus select-button"
                                disabled={!isSelectedRow}
                            />
                        </div>
                    )}
                    <div className={Styles.tableFilter}>
                        {filteredItemsStatus === RequestStatus.FETCHING ? (
                            <Loader />
                        ) : (
                            <AgGridTable
                                {...styleOptions}
                                ref={gridRef}
                                rowData={textFieldValue ? filteredItems : items}
                                columnDefs={columnDefs}
                                animateRows
                                rowSelection={{
                                    mode: 'multiRow',
                                    headerCheckbox: false,
                                    checkboxes: false,
                                }}
                                overlayNoRowsTemplate="Ничего не найдено"
                                onGridReady={onGridReady}
                                onRowDataUpdated={setCheckboxes}
                                onSelectionChanged={onRowsSelected}
                            />
                        )}
                    </div>
                </TableFilterContainer>
            )}
        </>
    );
};

CustomFilterWithGrid.displayName = 'CustomFilterWithGrid';
